import * as Yup from 'yup';
import { JOB_FORM_STEPS } from '@/raptorjob/domain/constants/jobFormLabels';
import { differenceInDays, getHours, getMinutes, getSeconds, isValid } from 'date-fns';
import { isFieldRequired, REQUIRED_FIELD_NAMES } from '@/raptorjob/application/utils/erpSettings';

export const ERPJobValidationSchema = (
  currentStep?: string,
  jobEndDateActive?: boolean,
  requiredFields?: Array<{ name: string; required: boolean }>,
  poRequired?: boolean
) => {
  const isVolumeRequired = isFieldRequired(requiredFields ?? [], REQUIRED_FIELD_NAMES.VOLUME);
  const isCallerRequired = isFieldRequired(requiredFields ?? [], REQUIRED_FIELD_NAMES.CALLER);

  return Yup.object({
    customerId: Yup.number().required('Customer is required').min(1, 'Please select a valid customer'),

    jobSiteId: Yup.number()
      .nullable()
      .when('customerId', {
        is: (customerId: number | null | undefined) => Boolean(customerId && customerId > 0),
        then: (schema) => schema.required('Job site is required').min(1, 'Please select a valid job site'),
        otherwise: (schema) => schema
      }),

    jobTypeId: Yup.number().required('Job type is required').min(1, 'Please select a valid job type'),

    startDate: Yup.date().typeError('Please enter a valid date.').required('Start date is required'),

    endDate: Yup.date()
      .nullable()
      .typeError('Please enter a valid date')
      .when([], {
        is: () => Boolean(jobEndDateActive),
        then: (schema) =>
          schema.required('Job End Date is required').when('startDate', {
            is: (startDate: Date | null | undefined) => Boolean(startDate),
            then: (s) => s.min(Yup.ref('startDate'), 'End date cannot be earlier than start date'),
            otherwise: (s) => s
          }),
        otherwise: (schema) =>
          schema.when('startDate', {
            is: (startDate: Date | null | undefined) => Boolean(startDate),
            then: (s) => s.min(Yup.ref('startDate'), 'End date cannot be earlier than start date'),
            otherwise: (s) => s
          })
      }),

    endTime: Yup.date().nullable(),

    startTime: Yup.date()
      .nullable()
      .test('time-sequence-onjob', function (value) {
        const { parent } = this;
        const startDate = parent['startDate'];
        const endDate = parent['endDate'];

        // Skip validation if endDate is greater than startDate (overnight job)
        if (startDate && endDate && isValid(startDate) && isValid(endDate) && differenceInDays(endDate, startDate) > 0) {
          return true;
        }

        const time1 = value;
        const time2 = parent['onJobTime'];

        if (time1 === null || time1 === undefined || time2 === null || time2 === undefined) return true;
        if (!(time1 instanceof Date) || !(time2 instanceof Date)) return true;
        if (isNaN(time1.getTime()) || isNaN(time2.getTime())) return true;

        const time1Seconds = getHours(time1) * 3600 + getMinutes(time1) * 60 + getSeconds(time1);
        const time2Seconds = getHours(time2) * 3600 + getMinutes(time2) * 60 + getSeconds(time2);

        const invalid = time1Seconds < time2Seconds;
        if (invalid) {
          return this.createError({
            message: 'Start Time cannot be less than On Job Time'
          });
        }
        return true;
      }),

    showUpTime: Yup.date().nullable(),

    leaveByTime: Yup.date()
      .nullable()
      .test('time-sequence-showup', function (value) {
        const { parent } = this;
        const startDate = parent['startDate'];
        const endDate = parent['endDate'];

        // Skip validation if endDate is greater than startDate (overnight job)
        if (startDate && endDate && isValid(startDate) && isValid(endDate) && differenceInDays(endDate, startDate) > 0) {
          return true;
        }

        const time1 = value;
        const time2 = parent['showUpTime'];

        if (time1 === null || time1 === undefined || time2 === null || time2 === undefined) return true;
        if (!(time1 instanceof Date) || !(time2 instanceof Date)) return true;
        if (isNaN(time1.getTime()) || isNaN(time2.getTime())) return true;

        const time1Seconds = getHours(time1) * 3600 + getMinutes(time1) * 60 + getSeconds(time1);
        const time2Seconds = getHours(time2) * 3600 + getMinutes(time2) * 60 + getSeconds(time2);

        const invalid = time1Seconds < time2Seconds;
        if (invalid) {
          return this.createError({
            message: 'Leave Time cannot be less than Show Up Time'
          });
        }
        return true;
      }),

    onJobTime: Yup.date()
      .nullable()
      .test('time-sequence-leaveby', function (value) {
        const { parent } = this;
        const startDate = parent['startDate'];
        const endDate = parent['endDate'];

        // Skip validation if endDate is greater than startDate (overnight job)
        if (startDate && endDate && isValid(startDate) && isValid(endDate) && differenceInDays(endDate, startDate) > 0) {
          return true;
        }

        const time1 = value;
        const time2 = parent['leaveByTime'];

        if (time1 === null || time1 === undefined || time2 === null || time2 === undefined) return true;
        if (!(time1 instanceof Date) || !(time2 instanceof Date)) return true;
        if (isNaN(time1.getTime()) || isNaN(time2.getTime())) return true;

        const time1Seconds = getHours(time1) * 3600 + getMinutes(time1) * 60 + getSeconds(time1);
        const time2Seconds = getHours(time2) * 3600 + getMinutes(time2) * 60 + getSeconds(time2);

        const invalid = time1Seconds < time2Seconds;
        if (invalid) {
          return this.createError({
            message: 'On Job Time cannot be less than Leave Time'
          });
        }
        return true;
      }),

    resourceStartTime: Yup.date().nullable(),

    resourceEndTime: Yup.date()
      .nullable()
      .when('resourceStartTime', {
        is: (resourceStartTime: Date) => Boolean(resourceStartTime),
        then: (schema) => schema.min(Yup.ref('resourceStartTime'), 'End time must be after start time')
      }),

    resourceShowUpTime: Yup.date().nullable(),

    resourceLeaveByTime: Yup.date().nullable(),

    resourceOnJobTime: Yup.date().nullable(),

    notesForCrew: Yup.string().max(255, 'Notes for crew must be less than 255 characters'),

    jobNotes: Yup.string().max(255, 'Job notes must be less than 255 characters'),

    estimatedVolume: isVolumeRequired
      ? Yup.number().required('Estimated volume is required').min(0, 'Estimated volume must be greater than or equal to 0')
      : Yup.number().nullable().min(0, 'Estimated volume must be greater than or equal to 0'),

    estimatedArea: Yup.number().nullable().min(0, 'Estimated area must be greater than or equal to 0'),

    estimatedDuration: Yup.number().nullable().min(0, 'Estimated hours must be greater than or equal to 0'),

    estimatedClearedTime: Yup.date().nullable().typeError('Estimated cleared time must be a valid date'),

    salesAgentId: Yup.number().required('Sales agent is required').min(1, 'Please select a valid sales agent'),

    cod: Yup.boolean().default(false),

    poNumber: poRequired ? Yup.string().required('PO Number is required').trim().min(1, 'PO Number is required') : Yup.string().optional(),

    vendorId: Yup.number().nullable(),

    callerId: isCallerRequired
      ? Yup.number().required('Caller is required').min(1, 'Please select a valid caller')
      : Yup.number().nullable(),

    calledAt: Yup.date().typeError('Called In Time must be a valid date').required('Called In Time is required'),

    confirmedBy: Yup.string().nullable(),

    confirmedAt: Yup.date().nullable(),

    dispatcherId: Yup.number().required('Dispatcher is required').min(1, 'Please select a valid dispatcher'),

    dispatchLocationId: Yup.number().required('Dispatch location is required').min(1, 'Please select a valid dispatch location'),

    extraSystemIds: Yup.array().of(Yup.number()).default([]),
    extraSystems: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required('Row ID is required'),
          extraSystemId: Yup.number().nullable(),
          name: Yup.string().nullable()
        })
      )
      .default([]),

    employees: Yup.array()
      .of(
        Yup.object({
          employeeId: Yup.number().required('Employee is required').min(1, 'Please select a valid employee'),
          employeeName: Yup.string().required('Employee name is required'),
          roleId: Yup.number().required('Role is required').min(1, 'Please select a valid role'),
          roleName: Yup.string().required('Role name is required')
        })
      )
      .default([]),

    equipmentClassId: Yup.number().nullable(),

    equipmentTypeId: Yup.number().nullable(),

    equipmentSizeId:
      currentStep === JOB_FORM_STEPS.RESOURCES
        ? Yup.number().required('Equipment size is required').min(1, 'Please select a valid equipment size')
        : Yup.number().nullable(),

    strictSize: Yup.boolean().default(false),

    equipmentMaxSizeId: Yup.number().nullable(),

    equipmentUnitId: Yup.number().nullable(),

    sequence: Yup.string().required('Sequence is required').min(1, 'Please select a valid sequence'),

    overnightJob: Yup.boolean().default(false)
  });
};

// Estimate interface
export interface Estimate {
  id: string;
  metricType: string;
  quantity: number | null;
  unit: string;
}

// Extra System interface
export interface ExtraSystem {
  id: string; // Unique row identifier
  extraSystemId: number | null; // Selected extra system ID
  name: string | null; // Selected extra system name
}

// Employee within a group interface
export interface GroupEmployee {
  employeeId: number | null;
  employeeName: string | null;
  roleId: number;
  roleName: string;
  status?: string[];
}

// Equipment details within a group
export interface GroupEquipment {
  equipmentId: number;
  equipmentName: string;
  equipmentClassId: number | null;
  equipmentClass: string | null;
  equipmentTypeId: number | null;
  equipmentType: string | null;
  equipmentSizeId: number | null;
  equipmentSize: string | null;
  equipmentStatus: string | null;
  equipmentMaxSize: string | null;
  equipmentMaxSizeId: number | null;
  strictSize: boolean;
}

// Group interface
export interface Group {
  id: string;
  employees: GroupEmployee[];
  equipment: GroupEquipment;
  items?: number[];
}

// Base form values type for initial values (allows null for all nullable fields)
export interface ERPJobFormValues {
  legacy: boolean;
  name?: string;
  customerId?: number | null;
  customerName?: string | null;
  jobSiteId?: number | null;
  jobTypeId?: number | null;
  jobDescription?: string; //description
  startDate?: Date | null;
  endDate?: Date | null;
  startTime?: Date | null;
  endTime?: Date | null;
  showUpTime?: Date | null;
  leaveByTime?: Date | null;
  onJobTime?: Date | null;
  resourceStartTime?: Date | null;
  resourceEndTime?: Date | null;
  resourceShowUpTime?: Date | null;
  resourceLeaveByTime?: Date | null;
  resourceOnJobTime?: Date | null;
  callerId?: number | null | string;
  calledAt?: Date | null;
  confirmedBy?: string | null;
  confirmedAt?: Date | null;
  cod?: boolean;
  poNumber?: string;
  salesAgentId?: number | null;
  salesRegionId?: number | null;
  vendorId?: number | null;
  flag1Id?: number | null;
  flag2Id?: number | null;
  flag3Id?: number | null;
  notesForCrew?: string; //crewNotes
  jobNotes?: string; // internalNotes
  estimatedVolume?: number | null;
  estimatedArea?: number | null;
  estimatedDuration?: number | null;
  estimatedClearedTime?: Date | string | null;
  dispatcherId?: number | null;
  dispatchLocationId?: number | null;
  extraSystems?: ExtraSystem[];
  employees: GroupEmployee[];
  equipment: GroupEquipment | null;
  items?: number[];
  equipmentClassId?: number | null;
  equipmentTypeId?: number | null;
  equipmentSizeId?: number | null;
  strictSize?: boolean;
  equipmentMaxSizeId?: number | null;
  equipmentUnitId?: number | null;
  sequence?: string | null;
  timeZone?: string | null; // IANA TimeZone ID (e.g., "America/New_York", "Europe/London")
  statusId?: number | null;
  overnightJob?: boolean;
  [key: string]: unknown;
}

export type ValidatedERPJobFormValues = Yup.InferType<ReturnType<typeof ERPJobValidationSchema>>;
